// src/game/shapes.ts
// Polyomino shape catalog (LOCKED contract — replaces the bar-only block model).
// Pure geometry; no DOM/RNG/time. Cells are normalized: sorted row-major, min row/col = 0.

import type { Cell, Side } from './types';

export type ShapeId =
  | 'dot' | 'duo' | 'tri' | 'sq' | 'iBar'
  | 'lTri' | 'jTri'
  | 'tTet' | 'sTet' | 'zTet' | 'lTet' | 'jTet' | 'oTet';

export interface ShapeDef {
  readonly id: ShapeId;
  readonly cells: readonly Cell[];
}

function c(row: number, col: number): Cell {
  return { row, col };
}

/** Translate to min row/col = 0, sort row-major, dedupe. */
export function normalizeShape(cells: readonly Cell[]): readonly Cell[] {
  if (cells.length === 0) {
    return [];
  }
  const minRow = Math.min(...cells.map((x) => x.row));
  const minCol = Math.min(...cells.map((x) => x.col));
  const seen = new Set<string>();
  const out: Cell[] = [];
  for (const x of cells) {
    const cell = c(x.row - minRow, x.col - minCol);
    const key = `${cell.row},${cell.col}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(cell);
    }
  }
  out.sort((a, b) => (a.row - b.row) || (a.col - b.col));
  return out;
}

function def(id: ShapeId, cells: readonly Cell[]): ShapeDef {
  return { id, cells: normalizeShape(cells) };
}

export const SHAPES: Readonly<Record<ShapeId, ShapeDef>> = {
  dot: def('dot', [c(0, 0)]),
  duo: def('duo', [c(0, 0), c(0, 1)]),
  tri: def('tri', [c(0, 0), c(0, 1), c(0, 2)]),
  iBar: def('iBar', [c(0, 0), c(0, 1), c(0, 2), c(0, 3)]),
  sq: def('sq', [c(0, 0), c(0, 1), c(1, 0), c(1, 1)]),
  lTri: def('lTri', [c(0, 0), c(1, 0), c(1, 1)]),
  jTri: def('jTri', [c(0, 1), c(1, 0), c(1, 1)]),
  tTet: def('tTet', [c(0, 0), c(0, 1), c(0, 2), c(1, 1)]),
  sTet: def('sTet', [c(0, 1), c(0, 2), c(1, 0), c(1, 1)]),
  zTet: def('zTet', [c(0, 0), c(0, 1), c(1, 1), c(1, 2)]),
  lTet: def('lTet', [c(0, 0), c(1, 0), c(2, 0), c(2, 1)]),
  jTet: def('jTet', [c(0, 1), c(1, 1), c(2, 0), c(2, 1)]),
  oTet: def('oTet', [c(0, 0), c(0, 1), c(1, 0), c(1, 1)]),
};

export function shapeBounds(cells: readonly Cell[]): { readonly rows: number; readonly cols: number } {
  const rows = Math.max(...cells.map((x) => x.row)) + 1;
  const cols = Math.max(...cells.map((x) => x.col)) + 1;
  return { rows, cols };
}

/** Distinct perpendicular line indices the shape spans on `side`, ascending. */
export function shapeLanes(cells: readonly Cell[], side: Side): readonly number[] {
  const vertical = side === 'top' || side === 'bottom';
  const set = new Set<number>();
  for (const x of cells) {
    set.add(vertical ? x.col : x.row);
  }
  return [...set].sort((a, b) => a - b);
}

/** Forward-most cell in each lane toward `side` (the leading silhouette edge). */
export function shapeLeadEdge(cells: readonly Cell[], side: Side): readonly Cell[] {
  const vertical = side === 'top' || side === 'bottom';
  const best = new Map<number, Cell>();
  for (const x of cells) {
    const lane = vertical ? x.col : x.row;
    const along = vertical ? x.row : x.col;
    const cur = best.get(lane);
    const curAlong = cur === undefined ? null : vertical ? cur.row : cur.col;
    const wantMax = side === 'bottom' || side === 'right';
    if (cur === undefined || (wantMax ? along > curAlong! : along < curAlong!)) {
      best.set(lane, x);
    }
  }
  return [...best.values()].sort((a, b) => (a.row - b.row) || (a.col - b.col));
}
